/**
 * What the UPS card says, worked out without React. RidgeWill/fleet-api#59.
 *
 * A CyberPower CP1500PFCLCD backs headless-1/2/3 and the Mac mini. Its USB
 * cable is in headless-2, whose fleet agent reads it from NUT and adds it to
 * its report. NUT shuts every box down at the UPS's low line (50% charge or
 * 5 minutes left, set in headless-2's ups.conf), and fleet-api reports those
 * thresholds, so this file reads them rather than knowing them.
 *
 * fleet-api has already decided `state` (silence is a state, 150 s, as for a
 * box), `on_battery` and `low_battery`; this file only puts them into words.
 */
import type { Ups, UpsOutage } from './fleet-api.js';
import { durationText } from './fleet-boxes.js';
import type { Tone } from './fleet-view.js';

export function upsStateView(ups: Ups): { tone: Tone; label: string } {
  const l = ups.last;
  if (ups.state === 'silent') {
    // Last heard on battery: most likely the boxes shut down, which is the case
    // that matters most and the one with the least to show.
    return l.on_battery
      ? { tone: 'danger', label: `Silent for ${durationText(ups.since_report_s)}, last seen on battery` }
      : { tone: 'warn', label: `No data for ${durationText(ups.since_report_s)}` };
  }
  if (l.on_battery) return { tone: 'danger', label: l.low_battery ? 'On battery, low: shutting down' : 'On battery' };
  if (l.low_battery) return { tone: 'warn', label: 'On line, recharging below the shutdown line' };
  return { tone: 'good', label: l.flags.includes('CHRG') ? 'On line, charging' : 'On line' };
}

/** `50% or 5 min left`, or null when the UPS reported neither threshold. */
export function shutdownLine(ups: Ups): string | null {
  const { charge_low_pct: charge, runtime_low_s: runtime } = ups.last;
  const parts = [charge === null ? null : `${Math.round(charge)}%`, runtime === null ? null : `${durationText(runtime)} left`].filter(
    (p): p is string => p !== null,
  );
  return parts.length === 0 ? null : parts.join(' or ');
}

/** `100%`, `84%`, or "Not reported". No dash: no em dashes in UI strings. */
export const pct = (v: number | null) => (v === null ? 'Not reported' : `${Math.round(v)}%`);

/** `30 W`, or "Not reported". */
export const watts = (v: number | null) => (v === null ? 'Not reported' : `${Math.round(v)} W`);

/** `124 V`, or "Not reported". */
export const volts = (v: number | null) => (v === null ? 'Not reported' : `${Math.round(v)} V`);

/**
 * How long an outage lasted, in words.
 *
 * A closed outage whose last report on battery and first back on line are
 * minutes apart is one the boxes slept through (they shut down at the low
 * line), so the length is only known to be at least the time seen on battery.
 */
export function outageLength(o: UpsOutage, ups: Ups): string {
  if (o.ended_at === null) {
    return ups.state === 'reporting' && ups.last.on_battery
      ? `${durationText(o.on_battery_s)} so far`
      : `at least ${durationText(o.on_battery_s)}, no word since`;
  }
  const gapS = (Date.parse(o.ended_at) - Date.parse(o.last_on_battery_at)) / 1000;
  // Two missed reports: the same allowance as a silent box.
  if (gapS > 150) return `at least ${durationText(o.on_battery_s)}`;
  // Seen on battery by one report only, with reports a minute either side on line.
  if (o.on_battery_s === 0) return 'under 2 min';
  return `about ${durationText((Date.parse(o.ended_at) - Date.parse(o.started_at)) / 1000)}`;
}
